// src/services/loggingService.ts
import axios from "axios";
import { API_BASE_URL } from "@/config";
import { getAccessToken } from "@/utils/tokenHelper";

// 🔧 FIXED: Generate session ID per track instead of per browser session (UUID v4)
let currentSessionId: string | null = null;

const generateUuidV4Like = (): string => {
  // Simple UUID v4 generator (not cryptographically strong, fine for client temp IDs)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const generateSessionId = (): string => {
  return generateUuidV4Like();
};

const getOrCreateSessionId = (): string => {
  if (!currentSessionId) {
    currentSessionId = generateSessionId();
  }
  return currentSessionId;
};

// 🔧 ADDED: Reset session ID for new tracks
const resetSessionId = (): void => {
  currentSessionId = null;
};

// Helper to check UUID shape
const isUuid = (v?: string): boolean => !!v && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(v);

// Enhanced playback state tracking
interface PlaybackState {
  trackId: string | null;
  startTime: number | null;
  totalListeningTime: number; // in milliseconds
  sessionId: string | null;
  isPaused: boolean;
  lastEventTime: number;
  isSessionActive: boolean;
  sessionStartTime: number | null;
  previousTrackId: string | null;
  nextTrackId: string | null;
  queuePosition: number | null;
  queueLength: number | null;
  loopMode: 'none' | 'track' | 'queue';
  shuffleEnabled: boolean;
  // 🔧 ADDED: Track transition tracking
  lastTrackId: string | null;
  playDurationMs: number;
  pauseDurationMs: number | null;
}

let playbackState: PlaybackState = {
  trackId: null,
  startTime: null,
  totalListeningTime: 0,
  sessionId: null,
  isPaused: false,
  lastEventTime: 0,
  isSessionActive: false,
  sessionStartTime: null,
  previousTrackId: null,
  nextTrackId: null,
  queuePosition: null,
  queueLength: null,
  loopMode: 'none',
  shuffleEnabled: false,
  // 🔧 ADDED: Track transition tracking
  lastTrackId: null,
  playDurationMs: 0,
  pauseDurationMs: null,
};

// Reset session when user starts a new browsing session
const resetSession = () => {
  currentSessionId = null;
  playbackState = {
    trackId: null,
    startTime: null,
    totalListeningTime: 0,
    sessionId: null,
    isPaused: false,
    lastEventTime: 0,
    isSessionActive: false,
    sessionStartTime: null,
    previousTrackId: null,
    nextTrackId: null,
    queuePosition: null,
    queueLength: null,
    loopMode: 'none',
    shuffleEnabled: false,
    // 🔧 ADDED: Track transition tracking
    lastTrackId: null,
    playDurationMs: 0,
    pauseDurationMs: null,
  };
};

// Enhanced analytics logging function with comprehensive tracking
export const logAnalyticsEvent = async ({
  trackId,
  trackTitle,
  artistId,
  artistName,
  eventType,
  trackDurationMs,
  skipReason,
  additionalData = {},
}: {
  trackId: string;
  trackTitle?: string;
  artistId?: string;
  artistName?: string;
  eventType: 'play_start' | 'play_resume' | 'pause' | 'track_complete' | 'skip_forward' | 'skip_backward' | 'seek' | 'session_interrupted';
  trackDurationMs?: number;
  skipReason?: string;
  additionalData?: Record<string, any>;
}) => {
  try {
    const token = getAccessToken();
    const now = Date.now();

    // 🔧 CRITICAL: For a new track, reset the session BEFORE building payload so the request uses the new session_id
    if (eventType === 'play_start') {
      resetSessionId();
    }

    // 🔧 ENHANCED: Calculate play duration since last event
    let playDurationMs = 0;
    if (playbackState.lastEventTime && playbackState.lastEventTime > 0) {
      const timeSinceLastEvent = now - playbackState.lastEventTime;
      // Only count as play duration if we were actually playing
      if (!playbackState.isPaused && playbackState.trackId === trackId) {
        playDurationMs = timeSinceLastEvent;
      }
    }

    // 🔧 ENHANCED: Calculate pause duration if applicable
    let pauseDurationMs: number | null = null;
    if (eventType === 'play_resume' && playbackState.pauseDurationMs !== null) {
      pauseDurationMs = playbackState.pauseDurationMs;
    }

    // 🔧 FIXED: Set play duration to 0 for seek events since they don't represent listening time
    if (eventType === 'seek') {
      playDurationMs = 0;
    }

    // Calculate listening time for this session with enhanced logic
    let listeningTimeMs = 0;
    if (playbackState.startTime && !playbackState.isPaused && playbackState.trackId === trackId) {
      const currentSessionTime = now - playbackState.startTime;
      listeningTimeMs = playbackState.totalListeningTime + currentSessionTime;
    } else {
      listeningTimeMs = playbackState.totalListeningTime;
    }

    // Calculate completion percentage
    let completionPercentage = 0;
    if (trackDurationMs && trackDurationMs > 0 && listeningTimeMs > 0) {
      completionPercentage = Math.min(100, Math.round((listeningTimeMs / trackDurationMs) * 100));
    }

    // Get browser and platform info
    const ua = navigator.userAgent;
    const isMobile = /Mobi|Android/i.test(ua);
    const browserMatch = ua.match(/(firefox|msie|chrome|safari|edg|opera|opr)/i);
    const browser = browserMatch ? browserMatch[0] : "unknown";

    // Guard artist_id to only send if it looks like a UUID
    const safeArtistId = isUuid(artistId) ? artistId : undefined;

    // Enhanced payload with comprehensive data
    const payload: Record<string, any> = {
      // Core track info
      track_id: trackId,
      track_title: trackTitle,
      artist_name: artistName,
      
      // Duration and timing
      track_duration_ms: trackDurationMs,
      listening_time_ms: listeningTimeMs,
      
      // Event details
      event_type: eventType,
      play_source: "web_app",
      
      // Session management - ensure UUID shape
      session_id: getOrCreateSessionId(),
      
      // Event-specific data
      completion_percentage: completionPercentage,
      skip_reason: skipReason,
      
      // Queue and playback context
      queue_position: playbackState.queuePosition,
      queue_length: playbackState.queueLength,
      loop_mode: playbackState.loopMode,
      shuffle_enabled: playbackState.shuffleEnabled,
      
      // Device and platform info
      platform: isMobile ? "mobile" : "desktop",
      device_type: browser,
      app_version: "web-1.0.0",
      
      // Session tracking
      is_session_active: playbackState.isSessionActive,
      session_duration_ms: playbackState.sessionStartTime ? now - playbackState.sessionStartTime : null,
      
      // Track transitions
      previous_track_id: playbackState.previousTrackId,
      next_track_id: playbackState.nextTrackId,
      
      // 🔧 ADDED: Enhanced timing data
      play_duration_ms: playDurationMs,
      pause_duration_ms: pauseDurationMs,
      
      // Additional context
      ...additionalData,
    };

    if (safeArtistId) {
      payload.artist_id = safeArtistId;
    }

    console.log(`🎵 Logging ${eventType} for track: ${trackTitle} (${trackId})`);
    console.log(`⏱️ Listening time: ${listeningTimeMs}ms, Completion: ${completionPercentage}%`);
    console.log(`🔄 Session: ${getOrCreateSessionId()}, Active: ${playbackState.isSessionActive}`);
    console.log(`📊 Play duration: ${playDurationMs}ms, Pause duration: ${pauseDurationMs}ms`);

    const response = await axios.post(
      `${API_BASE_URL}/analytics/log-play`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    
    console.log(`✅ ${eventType} logged successfully for: ${trackTitle || trackId}`);
    
    // Update playback state based on event type with enhanced logic
    updatePlaybackState(trackId, eventType, now, listeningTimeMs, playDurationMs, pauseDurationMs);
    
    // Handle session ID from response (server always returns a UUID)
    if (response.data?.session_id && response.data.session_id !== getOrCreateSessionId() && isUuid(response.data.session_id)) {
      currentSessionId = response.data.session_id;
      console.log(`🔄 Updated session ID to: ${currentSessionId}`);
    }
    
  } catch (error: any) {
    if (error.response) {
      console.error("Failed to log analytics:", {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
      });
    } else if (error.request) {
      console.error("No response received when logging analytics:", error.request);
    } else {
      console.error("Error setting up analytics request:", error.message);
    }
  }
};

// Enhanced internal playback state management
const updatePlaybackState = (trackId: string, eventType: string, timestamp: number, listeningTimeMs: number, playDurationMs: number, pauseDurationMs: number | null) => {
  switch (eventType) {
    case 'play_start':
      // 🔧 FIXED: Check if this is a new track and reset session if needed
      if (playbackState.trackId !== trackId) {
        if (playbackState.trackId) {
          console.log(`🔄 Track transition: ${playbackState.trackId} -> ${trackId}, resetting session`);
        }
        resetSessionId();
        playbackState.totalListeningTime = 0;
      }
      playbackState = {
        trackId,
        startTime: timestamp,
        totalListeningTime: 0,
        sessionId: getOrCreateSessionId(),
        isPaused: false,
        lastEventTime: timestamp,
        isSessionActive: true,
        sessionStartTime: timestamp,
        previousTrackId: playbackState.trackId,
        nextTrackId: null,
        queuePosition: null,
        queueLength: null,
        loopMode: 'none',
        shuffleEnabled: false,
        lastTrackId: trackId,
        playDurationMs: 0,
        pauseDurationMs: null,
      };
      break;

    case 'play_resume':
      if (playbackState.trackId !== trackId) {
        // Ignore stray resume for a different track
        break;
      }
      playbackState.startTime = timestamp;
      playbackState.isPaused = false;
      playbackState.lastEventTime = timestamp;
      playbackState.isSessionActive = true;
      playbackState.playDurationMs = playDurationMs;
      playbackState.pauseDurationMs = null;
      break;

    case 'pause':
      if (playbackState.trackId !== trackId) {
        // Ignore pause for a different track
        break;
      }
      if (playbackState.startTime) {
        const sessionTime = timestamp - playbackState.startTime;
        playbackState.totalListeningTime += sessionTime;
      }
      playbackState.startTime = null;
      playbackState.isPaused = true;
      playbackState.lastEventTime = timestamp;
      playbackState.playDurationMs = 0;
      playbackState.pauseDurationMs = pauseDurationMs;
      break;

    case 'track_complete':
      if (playbackState.trackId !== trackId) {
        // Don't mutate current state if this completion is for a previous track
        break;
      }
      if (playbackState.startTime) {
        const sessionTime = timestamp - playbackState.startTime;
        playbackState.totalListeningTime += sessionTime;
      }
      playbackState.startTime = null;
      playbackState.isPaused = false;
      playbackState.lastEventTime = timestamp;
      playbackState.previousTrackId = playbackState.trackId;
      playbackState.trackId = null;
      playbackState.playDurationMs = 0;
      playbackState.pauseDurationMs = null;
      break;

    case 'skip_forward':
    case 'skip_backward':
      if (playbackState.trackId !== trackId) {
        // Skip event for previous track: do not alter current track's timing state
        break;
      }
      if (playbackState.startTime) {
        const sessionTime = timestamp - playbackState.startTime;
        playbackState.totalListeningTime += sessionTime;
      }
      playbackState.startTime = null;
      playbackState.isPaused = false;
      playbackState.lastEventTime = timestamp;
      playbackState.previousTrackId = playbackState.trackId;
      playbackState.trackId = null;
      playbackState.playDurationMs = 0;
      playbackState.pauseDurationMs = null;
      break;

    case 'session_interrupted':
      if (playbackState.startTime) {
        const sessionTime = timestamp - playbackState.startTime;
        playbackState.totalListeningTime += sessionTime;
      }
      playbackState.startTime = null;
      playbackState.isPaused = true;
      playbackState.lastEventTime = timestamp;
      playbackState.isSessionActive = false;
      playbackState.sessionStartTime = null;
      playbackState.previousTrackId = playbackState.trackId;
      playbackState.trackId = null;
      playbackState.playDurationMs = 0;
      playbackState.pauseDurationMs = null;
      break;

    case 'seek':
      if (playbackState.trackId !== trackId) {
        break;
      }
      playbackState.lastEventTime = timestamp;
      playbackState.playDurationMs = 0;
      playbackState.pauseDurationMs = null;
      break;

    default:
      playbackState.lastEventTime = timestamp;
      break;
  }
  console.log(`🔄 Playback state updated: ${eventType} -> Track: ${playbackState.trackId}, Session: ${playbackState.isSessionActive}, Listening: ${playbackState.totalListeningTime}ms`);
};

// Legacy function for backward compatibility
export const logPlayback = async ({
  trackId,
  userId,
  playerType,
  platform,
  browser,
  track_title,
  artist_name,
}: {
  trackId: string;
  userId?: string;
  playerType: "desktop" | "mobile";
  platform: string;
  browser: string;
  track_title?: string;
  artist_name?: string;
}) => {
  // Convert to new analytics format
  await logAnalyticsEvent({
    trackId,
    trackTitle: track_title,
    artistName: artist_name,
    eventType: 'play_start',
  });
};

// Function to log play completion
export const logTrackComplete = async (
  trackId: string,
  trackTitle?: string,
  artistName?: string,
  trackDurationMs?: number,
  additionalData?: Record<string, any>,
  artistId?: string
) => {
  await logAnalyticsEvent({
    trackId,
    trackTitle,
    artistId,
    artistName,
    eventType: 'track_complete',
    trackDurationMs,
    additionalData,
  });
};

// Function to log pause events
export const logPause = async (
  trackId: string,
  trackTitle?: string,
  artistName?: string,
  trackDurationMs?: number,
  additionalData?: Record<string, any>,
  artistId?: string
) => {
  await logAnalyticsEvent({
    trackId,
    trackTitle,
    artistId,
    artistName,
    eventType: 'pause',
    trackDurationMs,
    additionalData,
  });
};

// Function to log resume events
export const logResume = async (
  trackId: string,
  trackTitle?: string,
  artistName?: string,
  trackDurationMs?: number,
  additionalData?: Record<string, any>,
  artistId?: string
) => {
  await logAnalyticsEvent({
    trackId,
    trackTitle,
    artistId,
    artistName,
    eventType: 'play_resume',
    trackDurationMs,
    additionalData,
  });
};

// Function to log skip events
export const logSkip = async (
  trackId: string,
  direction: 'forward' | 'backward',
  trackTitle?: string,
  artistName?: string,
  skipReason?: string,
  trackDurationMs?: number,
  additionalData?: Record<string, any>,
  artistId?: string
) => {
  await logAnalyticsEvent({
    trackId,
    trackTitle,
    artistId,
    artistName,
    eventType: direction === 'forward' ? 'skip_forward' : 'skip_backward',
    skipReason,
    trackDurationMs,
    additionalData,
  });
};

// Legacy function for updating play duration (now handled automatically)
export const updatePlayDuration = async ({
  trackId,
  userId,
  playDuration,
}: {
  trackId: string;
  userId?: string;
  playDuration: number;
}) => {
  // This is now handled automatically by the new analytics system
  console.log(`📊 Play duration tracking is now automatic for track ${trackId}`);
};

// Export session management functions
export { resetSession, getOrCreateSessionId };

// 🔧 ADDED: Function to handle track transitions (equivalent to mobile app's currentIndexStream logic)
export const handleTrackTransition = async (
  newTrackId: string,
  newTrackTitle?: string,
  newArtistName?: string,
  newTrackDurationMs?: number,
  transitionType: 'auto_advance' | 'manual_play' | 'skip' = 'auto_advance',
  additionalData?: Record<string, any>
) => {
  // If this is a different track, end the previous session and start a new one
  if (playbackState.trackId && playbackState.trackId !== newTrackId) {
    console.log(`🔄 Track transition detected: ${playbackState.trackId} -> ${newTrackId} (${transitionType})`);
    
    // Log play_start event for the new track
    await logAnalyticsEvent({
      trackId: newTrackId,
      trackTitle: newTrackTitle,
      artistName: newArtistName,
      eventType: 'play_start',
      trackDurationMs: newTrackDurationMs,
      additionalData: {
        'is_new_track': true,
        'previous_track_id': playbackState.trackId,
        'transition_type': transitionType,
        ...additionalData,
      },
    });
  }
};
